import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import {
  useGetCollectionTicketClaims,
  useGetTravellerBearers,
  useListCollectionTicketClaimMemberStatesHook,
  getListCollectionTicketClaimMemberStatesQueryKey,
} from '@/api/trafikportalen';
import { FormSection } from '@/components/FormSection';
import { FreePeriodBadge } from '@/components/Organizations/FreePeriodBadge';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { ClaimTimeline } from '@/components/Students/ClaimTimeline';
import { normalizeCardSerial } from '@/helpers/cardSerial';
import { getMembershipStatus } from '@/helpers/membershipStatus';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { receivesScheduledTickets } from '@/helpers/scheduledClaims';
import { useSchoolCardSerials } from '@/hooks/useSchoolCardSerials';
import type {
  CollectionTicketClaimDTO,
  CollectionTicketClaimMemberStateDTO,
} from '@/api/trafikportalen';

interface ClaimWithState {
  claim: CollectionTicketClaimDTO;
  memberState?: CollectionTicketClaimMemberStateDTO;
}

interface StudentClaimStatesProps {
  collectionId: string;
  collectionMemberId: string;
  travellerId: string;
  membership: {
    active: boolean;
    inactivate_at?: string;
  };
}

export const StudentClaimStates = ({
  collectionId,
  collectionMemberId,
  travellerId,
  membership,
}: StudentClaimStatesProps) => {
  const { data: ticketClaims } = useGetCollectionTicketClaims(collectionId, {
    query: { enabled: !!collectionId },
  });

  const { data: travellerBearers } = useGetTravellerBearers(travellerId);
  const bearerSerials = useMemo(
    () => travellerBearers?.alltix_bearers.map((bearer) => bearer.serial) ?? [],
    [travellerBearers],
  );
  const { schoolCardSerials, isPending: isSearchingCards } =
    useSchoolCardSerials(bearerSerials);
  const hasSchoolCard = bearerSerials.some(
    (serial) => !!serial && schoolCardSerials.has(normalizeCardSerial(serial)),
  );
  const showCardStep =
    !!travellerBearers && !isSearchingCards && !hasSchoolCard;

  const activeClaims = useMemo(() => {
    if (!ticketClaims) return [];
    return ticketClaims.filter((claim) => getPeriodStatus(claim) === 'active');
  }, [ticketClaims]);

  const listMemberStates = useListCollectionTicketClaimMemberStatesHook();

  const memberStateQueries = useQueries({
    queries: activeClaims.map((claim) => ({
      queryKey: getListCollectionTicketClaimMemberStatesQueryKey(claim.id),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        listMemberStates(claim.id, signal),
      enabled: !!claim.id,
    })),
  });

  const claimsWithStates = useMemo(() => {
    const result: ClaimWithState[] = [];
    for (let i = 0; i < activeClaims.length; i++) {
      const claim = activeClaims[i];
      const queryData = memberStateQueries[i]?.data;
      const memberState = queryData?.find(
        (s) => s.collection_member_id === collectionMemberId,
      );
      result.push({ claim, memberState });
    }
    return result;
  }, [activeClaims, memberStateQueries, collectionMemberId]);

  const visibleClaims = receivesScheduledTickets(membership)
    ? claimsWithStates
    : claimsWithStates.filter((entry) => entry.memberState);
  const inactivateAt =
    getMembershipStatus(membership) === 'will-inactivate'
      ? membership.inactivate_at
      : undefined;

  if (visibleClaims.length === 0) return null;

  return (
    <FormSection title={<FormattedMessage defaultMessage="Biljettstatus" />}>
      {visibleClaims.map(({ claim, memberState }) => (
        <div key={claim.id} className="rounded-lg bg-background-50 py-3">
          <p className="mb-2 flex items-center gap-2 text-sm font-medium text-text-700">
            <FormattedMessage defaultMessage="Period" />
            <PeriodDateRange from={claim.valid_from} to={claim.valid_to} />
            {claim.non_invoiced && <FreePeriodBadge />}
          </p>
          <ClaimTimeline
            memberState={memberState}
            inactivateAt={inactivateAt}
            showCardStep={showCardStep}
          />
        </div>
      ))}
    </FormSection>
  );
};
